<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue'

import Channel from './Channel.vue'
import Broadcast from './Broadcast.vue'
import BroadcastCard from './BroadcastCard.vue'
import { useEpgWidgetTracking } from '../../../../composables/use-epg-widget-tracking'

import type { EpgWidgetParagraph } from '../../../../utils/paragraph-schema/paragraphs'

type EpgWidget = EpgWidgetParagraph['epgWidget']
type EpgResultSet = NonNullable<EpgWidget['initialBroadcasts']>[number]
type BroadcastItem = EpgResultSet['broadcasts'][number]

const props = withDefaults(defineProps<{
  epgData?: EpgResultSet[],
  viewMode?: 'overview' | 'channel',
  // Topic pages (config.isThematic) show every broadcast as its own card instead of a channel-grouped list
  cardLayout?: boolean
}>(), {
  viewMode: 'overview'
})

const { trackEventInDataLayer } = useEpgWidgetTracking()

const origin = ref<string | null>(null)

// one entry per broadcast (paired with its channel) instead of channel-grouped rows, used for the card layout
const flattenedBroadcasts = computed(() => props.epgData?.flatMap(item => item.broadcasts.map(broadcast => ({ channel: item.channel, broadcast }))) ?? [])

const sliceBroadcasts = (broadcasts: BroadcastItem[]) => {
  if (broadcasts.length > 2) {
    console.warn('EpgSearch/BroadcastList.vue: more than two broadcasts available for channel')
  }
  // only show 2 broadcasts in overview mode
  return props.viewMode === 'overview' ? broadcasts.slice(0, 2) : broadcasts
}

onBeforeMount(() => {
  origin.value = window.location.origin
})
</script>

<template>
  <template v-if="cardLayout">
    <div class="bx-teaser-container bx-teaser-container--vod-epg-cards">
      <BroadcastCard v-for="pair in flattenedBroadcasts"
                     :key="pair.broadcast.originId"
                     :broadcast="pair.broadcast"
                     :channel="pair.channel"
                     :total-cards="flattenedBroadcasts.length" />
    </div>
  </template>

  <template v-else>
    <div v-for="(item, index) in epgData"
         :key="`${item.channel.originId}-${index}`"
         class="bx-epg-search__row"
         :class="{'bx-js--single-broadcast': item.broadcasts.length === 1}">
      <Channel v-if="viewMode !== 'channel'"
               :channel="item.channel"
               @click="trackEventInDataLayer('epg_channel_logo_click', { click_url: `${origin}${item.channel.url}` })" />
      <div>
        <Broadcast v-for="(broadcast, i) in sliceBroadcasts(item.broadcasts)"
                   :key="broadcast.originId"
                   :broadcast="broadcast"
                   @click="trackEventInDataLayer(i === 0 ? 'epg_program_click' : 'epg_upcoming_program_click', { click_url: `${origin}${broadcast.url}` })" />
      </div>
    </div>
  </template>
</template>
